
import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HelpCircle, MessageSquare, Phone, Mail, ArrowRight } from 'lucide-react';

const Support = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      category: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    
    // In a real application, you would send this data to your backend
    toast({
      title: "Support Request Submitted",
      description: "We've received your request and will respond within 24 hours.",
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      category: '',
      message: ''
    });
  };

  const supportCategories = [
    {
      icon: <HelpCircle className="h-8 w-8 text-purple-600" />,
      title: "General Inquiries",
      description: "Questions about our services, features, or platform information."
    },
    {
      icon: <MessageSquare className="h-8 w-8 text-purple-600" />,
      title: "Technical Support",
      description: "Issues with your account, app features, or technical problems."
    },
    {
      icon: <Mail className="h-8 w-8 text-purple-600" />,
      title: "Billing & Payments",
      description: "Questions about charges, subscriptions, or payment methods."
    }
  ];

  const faqs = [
    {
      question: "How do I reset my password?",
      answer: "You can reset your password by clicking on the 'Forgot Password' link on the login page. We'll send you an email with instructions to create a new password."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "To cancel your subscription, go to your Account Settings, select the 'Subscription' tab, and click on 'Cancel Subscription'. Your premium features will remain active until the end of your current billing period."
    },
    {
      question: "How can I report inappropriate behavior?",
      answer: "You can report any user by visiting their profile and clicking the 'Report' button. Fill out the form with details about the issue, and our moderation team will review it promptly."
    },
    {
      question: "Why was my account suspended?",
      answer: "Accounts may be suspended for violating our terms of service. Common reasons include inappropriate content, harassment, or suspicious activity. Please contact support for specific details about your account."
    },
    {
      question: "How long does it take to get a response from support?",
      answer: "We typically respond to support inquiries within 24 hours. For urgent matters, please mark your request as 'Urgent' when submitting your ticket."
    }
  ];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-pink-gradient text-gradient">Customer Support</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We're here to help! Choose from the options below to get the assistance you need.
          </p>
        </div>

        {/* Support Categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {supportCategories.map((category, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-4">
                  {category.icon}
                </div>
                <CardTitle className="text-xl">{category.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="mb-4">{category.description}</CardDescription>
                <Button variant="outline" className="border-purple-500 text-purple-700 hover:bg-purple-50 mt-2">
                  Get Help <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="contact" className="w-full mb-16">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="contact">Contact Us</TabsTrigger>
            <TabsTrigger value="faq">Quick Solutions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="contact" className="mt-0">
            <Card>
              <CardHeader>
                <CardTitle>Send Us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input 
                        id="name" 
                        name="name" 
                        placeholder="Your name" 
                        value={formData.name}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        placeholder="Your email" 
                        value={formData.email}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input 
                        id="subject" 
                        name="subject" 
                        placeholder="Subject of your inquiry" 
                        value={formData.subject}
                        onChange={handleChange}
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select value={formData.category} onValueChange={handleSelectChange}>
                        <SelectTrigger id="category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="account">Account Issues</SelectItem>
                          <SelectItem value="billing">Billing & Payments</SelectItem>
                          <SelectItem value="technical">Technical Problems</SelectItem>
                          <SelectItem value="safety">Safety Concerns</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message</Label>
                    <Textarea 
                      id="message" 
                      name="message" 
                      placeholder="Please describe your issue in detail" 
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-pink-gradient hover:opacity-90">
                    Submit Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>
                  Quick solutions to common questions and issues.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  ))}
                  
                  <div className="pt-4 text-center">
                    <p className="mb-4 text-gray-600">
                      Can't find what you're looking for? Contact our support team directly.
                    </p>
                    <Button className="bg-pink-gradient hover:opacity-90">
                      View All FAQs
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Contact information */}
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Other Ways to Reach Us</h2>
            <p className="text-gray-600">
              Our team is available to help you through multiple channels
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center pt-6">
                <Phone className="h-10 w-10 text-purple-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Call Us</h3>
                <p className="text-gray-600 text-center mb-1">Monday to Friday, 9am-5pm</p>
                <p className="font-medium">+1 (800) 123-4567</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center pt-6">
                <Mail className="h-10 w-10 text-purple-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Email Support</h3>
                <p className="text-gray-600 text-center mb-1">Response within 24 hours</p>
                <p className="font-medium">support@pinkparadise.com</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center pt-6">
                <MessageSquare className="h-10 w-10 text-purple-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Live Chat</h3>
                <p className="text-gray-600 text-center mb-1">Available 24/7 for premium users</p>
                <Button variant="outline" className="mt-2">Start Chat</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Support;
